import type { FetchContext, FetchResponse } from "ofetch";
import { useAuthStore } from "~/stores/auth/store";
import type { JwtPayload } from "~/stores/auth/types";
import { jwtDecode } from "jwt-decode";

export type ClientType = "v1" | "v4" | "chat" | "event-service";

export default defineNuxtPlugin({
  setup(nuxtApp) {
    const config = useRuntimeConfig();
    const authStore = useAuthStore();
    const route = useRoute();

    const apiV1 = $fetch.create({
      baseURL: `${config.public.apiBase}/properties/${config.public.propertyId}`,
      onRequest(context: FetchContext): Promise<void> | void {
        const headers = new Headers(context.options.headers);
        if (!headers.has("Authorization")) {
          const accessToken = useCookie("access_token");
          headers.set("X-Redirect-Host", config.public.siteUrl);
          headers.set(
            "Authorization",
            `Bearer ${accessToken.value || config.public.deviceToken}`,
          );
        }
        context.options.headers = headers;
      },
      onResponse(
        context: FetchContext & { response: FetchResponse<ResponseType> },
      ): Promise<void> | void {
        const token = context.response.headers.get("Authtoken");

        if (token) {
          nuxtApp.runWithContext(() => authStore.setAuthenticated(token));
        }
      },
    });

    const apiV4 = $fetch.create({
      baseURL: `${config.public.apiBase}/v4/api`,
      onRequest(context: FetchContext) {
        const headers = new Headers(context.options.headers);
        if (!headers.has("Authorization")) {
          const accessToken = useCookie("access_token");
          headers.set(
            "Authorization",
            `Bearer ${accessToken.value || config.public.deviceToken}`,
          );
          headers.set("X-Redirect-Host", config.public.siteUrl);
        }
        context.options.headers = headers;
      },
      async onResponse(
        context: FetchContext & { response: FetchResponse<ResponseType> },
      ) {
        const pageName = route.name;
        const token = context.response.headers.get("Authtoken");

        if (context.response.status === 401) {
          const endpoint = context.request?.toString() || "unknown";
          const accessToken = useCookie("access_token");
          const tokenData = accessToken.value
            ? jwtDecode<JwtPayload>(accessToken.value)
            : null;

          useBugsnag().notify({
            name: "401 Error",
            message: `endpoint: ${endpoint}${tokenData?.email ? `, email: ${tokenData.email}` : ""}`,
          });
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
        if (
          context.response.status === 401 &&
          pageName !== "pokerGO::page:login-page" &&
          token == "LOGOUT"
        ) {
          nuxtApp.runWithContext(() => authStore.handleLogout());
        } else if (token) {
          nuxtApp.runWithContext(() => authStore.setAuthenticated(token));
        }
      },
    });

    const chatApi = $fetch.create({
      baseURL: `${config.public.chatApiAddress}/v1/api/`,
      onRequest(context: FetchContext): Promise<void> | void {
        const headers = new Headers(context.options.headers);
        if (!headers.has("Authorization")) {
          const accessToken = useCookie("access_token");
          headers.set("X-Redirect-Host", config.public.siteUrl);
          headers.set(
            "Authorization",
            `Bearer ${accessToken.value || config.public.deviceToken}`,
          );
        }
        context.options.headers = headers;
      },
      onResponse(
        context: FetchContext & { response: FetchResponse<ResponseType> },
      ): Promise<void> | void {
        const token = context.response.headers.get("Authtoken");

        if (token) {
          nuxtApp.runWithContext(() => authStore.setAuthenticated(token));
        }
      },
    });

    const eventServiceApi = $fetch.create({
      baseURL: `${config.public.videoProgressApiAddress}/api/v2/`,
      onRequest(context: FetchContext): Promise<void> | void {
        const headers = new Headers(context.options.headers);
        if (!headers.has("Authorization")) {
          const accessToken = useCookie("access_token");
          headers.set("X-Redirect-Host", config.public.siteUrl);
          headers.set(
            "Authorization",
            `Bearer ${accessToken.value || config.public.deviceToken}`,
          );
        }
        context.options.headers = headers;
      },
    });

    function api(type: ClientType) {
      if (type === "chat") {
        return chatApi;
      }

      if (type === "event-service") {
        return eventServiceApi;
      }

      if (type === "v1") {
        return apiV1;
      }

      return apiV4;
    }

    return {
      provide: {
        api,
      },
    };
  },
});
